import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import {
  AiActionItemsBlock,
  AiAssistantBlock,
  AiAssetListBlock,
  AiBranchNarrationBlock,
  AiCardNarrationBlock,
  AiChatMessage,
  AiFollowUpBlock,
  AiNarrationGraphBlock,
  AiOnlyNarrationBlock,
  AiPromptAudit,
  AiTaskContextBlock,
  AiTaskLineageBlock,
  AiTaskListBlock,
} from '../../models/ai-assistant.models';
import { AiFeedbackActionsComponent } from '../ai-feedback-actions/ai-feedback-actions.component';
import { ResponseAssetListComponent } from '../response-asset-list/response-asset-list.component';
import { ResponseCardNarrationComponent } from '../response-card-narration/response-card-narration.component';
import { ResponseFollowUpComponent } from '../response-follow-up/response-follow-up.component';
import { ResponseNarrationGraphComponent } from '../response-narration-graph/response-narration-graph.component';
import { ResponseOnlyNarrationComponent } from '../response-only-narration/response-only-narration.component';
import { ResponseTaskListComponent } from '../response-task-list/response-task-list.component';
import { ResponseActionItemsComponent } from '../response-action-items/response-action-items.component';
import { ResponseTaskContextComponent } from '../response-task-context/response-task-context.component';
import { ResponseTaskLineageComponent } from '../response-task-lineage/response-task-lineage.component';

@Component({
  selector: 'app-ai-message-shell',
  standalone: true,
  imports: [
    CommonModule,
    AiFeedbackActionsComponent,
    ResponseActionItemsComponent,
    ResponseAssetListComponent,
    ResponseCardNarrationComponent,
    ResponseFollowUpComponent,
    ResponseNarrationGraphComponent,
    ResponseOnlyNarrationComponent,
    ResponseTaskListComponent,
    ResponseTaskContextComponent,
    ResponseTaskLineageComponent,
  ],
  templateUrl: './ai-message-shell.component.html',
  styleUrl: './ai-message-shell.component.css',
})
export class AiMessageShellComponent {
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) message!: AiChatMessage;
  @Input() agentName = 'Security Assistant';
  @Output() auditRequested = new EventEmitter<AiPromptAudit>();
  @Output() followUpSelected = new EventEmitter<string>();

  asTaskList(block: AiAssistantBlock): AiTaskListBlock {
    return block as AiTaskListBlock;
  }

  asAssetList(block: AiAssistantBlock): AiAssetListBlock {
    return block as AiAssetListBlock;
  }

  asNarrationGraph(block: AiAssistantBlock): AiNarrationGraphBlock {
    return block as AiNarrationGraphBlock;
  }

  asCardNarration(block: AiAssistantBlock): AiCardNarrationBlock {
    return block as AiCardNarrationBlock;
  }

  asOnlyNarration(block: AiAssistantBlock): AiOnlyNarrationBlock {
    return block as AiOnlyNarrationBlock;
  }

  asFollowUp(block: AiAssistantBlock): AiFollowUpBlock {
    return block as AiFollowUpBlock;
  }

  asBranchNarration(block: AiAssistantBlock): AiBranchNarrationBlock {
    return block as AiBranchNarrationBlock;
  }

  asTaskContext(block: AiAssistantBlock): AiTaskContextBlock {
    return block as AiTaskContextBlock;
  }

  asTaskLineage(block: AiAssistantBlock): AiTaskLineageBlock {
    return block as AiTaskLineageBlock;
  }

  asActionItems(block: AiAssistantBlock): AiActionItemsBlock {
    return block as AiActionItemsBlock;
  }

  displayTime(value: string): string {
    return new Date(value).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  hasBlockType(type: string): boolean {
    return (this.message.blocks ?? []).some(b => b.type === type);
  }

  renderMarkdown(text: string): SafeHtml {
    const html = marked(this.prepareMarkdown(text), { async: false });
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  /** Convert backend mixed-format text to clean markdown before rendering. */
  prepareMarkdown(text: string): string {
    let out = text
      .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/\{+[^{}]*\}+/g, '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .trim();

    // Format A: " --- " section markers (collapse newlines then split)
    if (/ --- /.test(out) || /\n---[\s\n]/.test(out)) {
      return this.processDashSections(out);
    }
    // Format B: structured sections with -- header + known section name lines
    return this.processStructured(out);
  }

  // ── Format A ─────────────────────────────────────────────────────────────
  private processDashSections(text: string): string {
    const flat = text.replace(/\n/g, ' ').replace(/\s{2,}/g, ' ');
    const result: string[] = [];
    for (const part of flat.split(/ --- /)) {
      const t = part.trim();
      if (!t) continue;
      const m = t.match(/^([A-Z][A-Za-z0-9 \/.]+?)\s*[:\-]\s+([\s\S]*)$/);
      if (m) {
        result.push(`### ${m[1].trim()}\n`);
        result.push(this.expandBody(m[2].trim()));
      } else {
        result.push(this.expandBody(t));
      }
      result.push('');
    }
    return result.join('\n');
  }

  // ── Format B ─────────────────────────────────────────────────────────────
  private readonly SECTION_NAMES = [
    'Execution Status', 'Action Items', 'Assets', 'Risk Reduction',
    'Evidence Status', 'Next Steps', 'Task Context', 'Summary',
  ];

  private processStructured(text: string): string {
    // Remove artifact lines that are only pipe chars
    const lines = text
      .split('\n')
      .filter((l) => !/^\|+\s*$/.test(l.trim()));

    const result: string[] = [];

    for (const line of lines) {
      const t = line.trim();
      if (!t) { result.push(''); continue; }

      // -- double-dash header → bold metadata row
      if (t.startsWith('-- ')) {
        result.push(this.formatMeta(t.slice(3)));
        result.push('');
        continue;
      }

      // Known section name at start of line
      const sec = this.SECTION_NAMES.find(
        (s) => t.startsWith(s + ' ') || t.startsWith(s + ':'),
      );
      if (sec) {
        const body = t.slice(sec.length).replace(/^[\s:]+/, '').trim();
        result.push('');
        result.push(`### ${sec}`);
        result.push('');
        if (body) result.push(this.expandBody(body));
        continue;
      }

      // Tab-separated row — delegate to plain-line handler
      if (line.includes('\t')) {
        const cells = line.split('\t').map((c) => c.trim()).filter(Boolean);
        if (cells.length > 1) { result.push('| ' + cells.join(' | ') + ' |'); continue; }
        result.push(this.processPlainLines(line)); continue;
      }

      // Inline next-step bullets: "- text - text"
      if (/^\s*-\s+\S/.test(t) && t.includes(' - ')) {
        result.push(t.replace(/\s+-\s+/g, '\n- '));
        continue;
      }

      result.push(this.expandBody(t));
    }

    return result.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  }

  private formatMeta(raw: string): string {
    return raw
      .split(/\s*\|\s*/)
      .filter((p) => p.trim())
      .map((p) => {
        const i = p.indexOf(':');
        return i > 0
          ? `**${p.slice(0, i).trim()}:** ${p.slice(i + 1).trim()}`
          : p.trim();
      })
      .join('  ·  ');
  }

  // ── Plain newlined + tab tables (fallback) ────────────────────────────────
  private processPlainLines(text: string): string {
    const lines = text.split('\n');
    const result: string[] = [];
    let tableRows: string[][] = [];
    const flush = () => {
      if (!tableRows.length) return;
      if (tableRows.length === 1) { result.push(tableRows[0].join(' ')); tableRows = []; return; }
      const cols = Math.max(...tableRows.map((r) => r.length));
      const pad = (row: string[]) => { while (row.length < cols) row.push(''); return row.map((c) => c || ' '); };
      result.push('| ' + pad(tableRows[0]).join(' | ') + ' |');
      result.push('| ' + Array(cols).fill('---').join(' | ') + ' |');
      for (let i = 1; i < tableRows.length; i++) result.push('| ' + pad(tableRows[i]).join(' | ') + ' |');
      result.push('');
      tableRows = [];
    };
    for (const line of lines) {
      const t = line.trim();
      if (line.includes('\t')) {
        const cells = line.split('\t').map((c) => c.trim());
        while (cells.length && !cells[cells.length - 1]) cells.pop();
        if (cells.length > 1) { tableRows.push(cells); continue; }
      }
      flush();
      if (/^[A-Z][A-Za-z \/]+:\s*$/.test(t)) result.push(`### ${t.replace(/:$/, '')}`);
      else result.push(line);
    }
    flush();
    return result.join('\n');
  }

  private expandBody(text: string): string {
    if (!text.includes('||')) return text;

    // The content before the first || may contain | as text separators (summary
    // sentences like "Total: 27 | By Category: 5").  We detect where the *actual*
    // table header starts by finding the longest run of short column-name cells
    // at the END of the pre-|| segment.
    const doubleIdx = text.indexOf('||');
    const firstSegment = text.slice(0, doubleIdx);
    const rest = text.slice(doubleIdx + 2).split('||');

    const { preText, headerRow } = this.detectTableHeader(firstSegment);

    const tableRows: string[] = [];
    if (headerRow) tableRows.push(headerRow);

    let sepInserted = false;

    for (const seg of rest) {
      const s = seg.trim();
      if (!s) continue;

      // Separator row (only dashes/pipes/colons)
      if (/^[|:\-\s]+$/.test(s)) {
        if (tableRows.length && !sepInserted) {
          const cols = (tableRows[0].match(/\|/g) ?? []).length - 1;
          tableRows.push('| ' + Array(Math.max(cols, 1)).fill('---').join(' | ') + ' |');
          sepInserted = true;
        }
        continue;
      }

      // Auto-insert separator before first data row
      if (tableRows.length && !sepInserted) {
        const cols = (tableRows[0].match(/\|/g) ?? []).length - 1;
        tableRows.push('| ' + Array(Math.max(cols, 1)).fill('---').join(' | ') + ' |');
        sepInserted = true;
      }

      let row = s;
      if (!row.startsWith('|')) row = '| ' + row;
      if (!row.endsWith('|')) row = row + ' |';
      tableRows.push(row);
    }

    const parts: string[] = [];
    if (preText) parts.push(preText);
    if (tableRows.length) parts.push(tableRows.join('\n'));
    return parts.join('\n\n');
  }

  /** Identify where actual table column headers begin inside a pipe-delimited string.
   *  Summary sentences use | as text separators (long, contain colons/periods).
   *  Column names are short, no colons, no periods, ≤ 3 words. */
  private detectTableHeader(segment: string): { preText: string; headerRow: string } {
    const cells = segment.split('|');

    const isColName = (cell: string): boolean => {
      const t = cell.trim();
      if (!t || t.length > 22) return false;
      if (t.includes(':') || (t.includes('.') && t.length > 6)) return false;
      return t.split(/\s+/).filter(Boolean).length <= 3;
    };

    // Walk from the end — find the first cell that breaks the column-name run
    let headerStart = cells.length;
    for (let i = cells.length - 1; i >= 0; i--) {
      if (isColName(cells[i])) headerStart = i;
      else break;
    }

    const headerCells = cells.slice(headerStart).map((c) => c.trim()).filter(Boolean);
    const headerRow = headerCells.length >= 2 ? '| ' + headerCells.join(' | ') + ' |' : '';
    const preText = (headerRow ? cells.slice(0, headerStart).join('|') : segment).trim();

    return { preText, headerRow };
  }
}
